import { useCallback, useState, useMemo, useRef, useEffect, useEffectEvent } from "react";
import type { OnboardingSlide } from "@boundary/widgets/onboarding";

interface UseOnboardingOptions<TData extends object, T extends string> {
  slides: OnboardingSlide<TData, T>[];
  startingSlide: T;
  onComplete: (data: TData) => void | Promise<void>;
  initialData?: Partial<TData>;
}

export function useOnboarding<TData extends object, T extends string>({
  slides,
  startingSlide,
  onComplete: onCompleteFn,
  initialData
}: UseOnboardingOptions<TData, T>) {
  const [step, setStep] = useState<T>(startingSlide);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TData>((initialData ?? {}) as TData);
  const dataRef = useRef<TData>((initialData ?? {}) as TData);

  // Отслеживаем изменения startingSlide из URL и обновляем step
  useEffect(() => {
    if (startingSlide && slides.some(slide => slide.step === startingSlide)) {
      setStep(startingSlide);
    }
  }, [startingSlide, slides]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const currentStep = useMemo(
    () => slides.find((slide) => slide.step === step),
    [slides, step]
  );

  const currentStepIndex = useMemo(
    () => slides.findIndex((slide) => slide.step === step),
    [slides, step]
  );

  const canGoNext = useMemo(
    () => currentStepIndex < slides.length - 1,
    [currentStepIndex, slides.length]
  );

  const canGoPrev = useMemo(() => currentStepIndex > 0, [currentStepIndex]);

  const goNext = useCallback(() => {
    if (!canGoNext) return;
    const nextSlide = slides[currentStepIndex + 1];
    if (nextSlide) {
      setStep(nextSlide.step);
    }
  }, [canGoNext, currentStepIndex, slides]);

  const goPrev = useCallback(() => {
    if (!canGoPrev) return;
    const prevSlide = slides[currentStepIndex - 1];
    if (prevSlide) {
      setStep(prevSlide.step);
    }
  }, [canGoPrev, currentStepIndex, slides]);

  const handleComplete = useEffectEvent(
    async (nextSlide: T | null, newData?: Partial<TData>) => {
      setLoading(true);

      try {
        if (newData) {
          setData((currentData) => {
            const updated = { ...currentData, ...newData } as TData;
            dataRef.current = updated;
            return updated;
          });
        }

        if (nextSlide) {
          setStep(nextSlide);
        } else {
          const finalData = newData
            ? { ...dataRef.current, ...newData }
            : dataRef.current;
          await onCompleteFn(finalData);
        }
      } finally {
        setLoading(false);
      }
    }
  );

  return {
    currentStep,
    data,
    loading,
    onComplete: handleComplete,
    goNext,
    goPrev
  };
}
